import { Outlet, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import { makeLogger } from '@/utils/logger/logger.ts';

const logger = makeLogger('_authenticated');
import { Box, Flex } from '@chakra-ui/react';
import Sidebar from '@/components/common/sidebar/Sidebar.tsx';
import { HouseProvider, useHouseContext } from '@/context/HouseContext.tsx';
import { AppNavigationProvider } from '@/context/AppNavigationContext.tsx';
import { SidebarCollapseProvider } from '@/context/SidebarCollapseContext.tsx';
import { UpdateStatusProvider } from '@/context/UpdateStatusContext';
import { TaskProvider } from '@/context/house/tasks/TaskContext.tsx';
import MobileNavBar from '@/components/navigation/MobileNavBar.tsx';
import { HouseTaskManagerProvider } from '@/context/house/tasks/HouseTaskManagerContext.tsx';
import GlobalOverlays from '@/components/common/GlobalOverlays.tsx';
import React from 'react';
import { House } from '@/client';
import { RecordStatusProvider } from '@/context/house/documents/RecordStatusContext.tsx';
import { propertyNavigateOptions, propertyRoutes, Routes } from '@/routes.ts';
import { RightSidePanelProvider } from '@/context/RightSidePanelContext';
import { safeGetItem } from '@/utils/storage/storageUtils.ts';
import { ACCESS_TOKEN_KEY } from '@/utils/storage/constants.ts';
import { useInactivityLogout } from '@/hooks/auth/useInactivityLogout';
import { TermsComplianceObserver } from '@/components/legal/TermsComplianceObserver';
import { HouseNotificationsProvider } from '@/context/HouseNotificationsContext';
import NotificationsDrawer from '@/components/notifications/NotificationsDrawer';
import { useLeftPanelState } from '@/hooks/ui/useLeftPanelState';
import { ChatHistoryProvider, useChatHistory } from '@/context/ChatHistoryContext';
import { readUsersMeUsersMeGetOptions, readHousesHousesGetOptions } from '@/client/@tanstack/react-query.gen';
import { hasIncompleteProfile } from '@/utils/userUtils';
import { isHouseSetupComplete } from '@/utils/houseUtils';
import { hasAdminRole } from '@/utils/roleUtils';
import { queryClient } from '@/lib/queryClient';
import { DesktopPropertyHeaderPortalProvider } from '@/context/DesktopPropertyHeaderPortalContext';
import { AppFooterPortalProvider } from '@/context/AppFooterPortalContext';
import { PageScrollContainerProvider } from '@/context/PageScrollContainerContext';

type PageLayoutProps = {
	showSidebar: boolean;
	showFooter: boolean;
	currentPath: string;
};

const PageLayout = ({ showSidebar, showFooter, currentPath }: PageLayoutProps) => {
	// Manage drawer states at layout level
	const notificationsDrawer = useLeftPanelState();
	const {
		isOpen: isChatHistoryOpen,
		open: openChatHistory,
		close: closeChatHistory,
		toggle: toggleChatHistory,
	} = useChatHistory();
	const { selectedHouse } = useHouseContext();
	const navigate = useNavigate();

	const toggleNotifications = () => {
		closeChatHistory();
		if (notificationsDrawer.isOpen) {
			notificationsDrawer.close();
			return;
		}
		notificationsDrawer.open();
	};

	const handleSidebarInteract = (event: React.PointerEvent) => {
		const target = event.target;
		if (target instanceof Element && target.closest('[data-sidebar-alerts-trigger]')) {
			return;
		}
		if (notificationsDrawer.isOpen) {
			notificationsDrawer.close();
		}
	};

	const handleSidebarItemClick = () => {
		closeChatHistory();
		if (notificationsDrawer.isOpen) {
			notificationsDrawer.close();
		}
	};

	const toggleChatHistoryPanel = () => {
		const publicId = selectedHouse?.public_id;
		const dashboardPath = publicId ? propertyRoutes(publicId).dashboard : Routes.PROPERTIES;
		const isOnDashboard = currentPath === dashboardPath;

		// On the dashboard the open-state is authoritative (drawer is mounted, reflects state).
		// Off the dashboard the drawer isn't mounted, so the state can be stale "open" from a
		// prior session — ignore it and always navigate + open.
		if (isOnDashboard && isChatHistoryOpen) {
			closeChatHistory();
			return;
		}
		if (notificationsDrawer.isOpen) {
			notificationsDrawer.close();
		}
		if (!isOnDashboard) {
			if (publicId) {
				navigate(propertyNavigateOptions(publicId));
			} else {
				navigate({ to: Routes.PROPERTIES });
			}
			openChatHistory();
			return;
		}
		toggleChatHistory();
	};

	// Ref for main content container to use as custom portal container for drawers
	const mainContentRef = React.useRef<HTMLDivElement>(null);
	const [desktopHeaderPortalContainer, setDesktopHeaderPortalContainer] = React.useState<HTMLDivElement | null>(null);
	const handleDesktopHeaderPortalRef = React.useCallback((node: HTMLDivElement | null) => {
		setDesktopHeaderPortalContainer(node);
	}, []);
	const [footerPortalContainer, setFooterPortalContainer] = React.useState<HTMLDivElement | null>(null);
	const handleFooterPortalRef = React.useCallback((node: HTMLDivElement | null) => {
		setFooterPortalContainer(node);
	}, []);

	return (
		<DesktopPropertyHeaderPortalProvider value={desktopHeaderPortalContainer}>
			<AppFooterPortalProvider value={footerPortalContainer}>
				<PageScrollContainerProvider value={mainContentRef}>
					<Flex direction="column" h="100dvh" overflow="hidden">
						{/* Mobile Navigation Bar - only shows on mobile */}
						{showSidebar && (
							<MobileNavBar
								onOpenNotifications={toggleNotifications}
								onToggleChatHistory={toggleChatHistoryPanel}
							/>
						)}

						<Flex
							flex="1"
							minH={0}
							overflow="hidden"
							bg={showSidebar ? { base: 'transparent', md: 'sidebar.surface' } : undefined}
						>
							{/* Desktop Sidebar - hidden on mobile */}
							{showSidebar && (
								<Flex display={{ base: 'none', md: 'flex' }}>
									<Sidebar
										onOpenNotifications={toggleNotifications}
										onToggleChatHistory={toggleChatHistoryPanel}
										onItemClick={handleSidebarItemClick}
										onInteract={handleSidebarInteract}
									/>
								</Flex>
							)}

							{/* Main content area — Linear-style inset card on desktop, edge-to-edge on mobile.
				    Split: outer owns the rounded border + clip, inner owns the scroll. Keeps the
				    scrollbar from poking past the rounded corners on Chromium. */}
							<Flex
								flex="1"
								minH={0}
								direction="column"
								position="relative"
								overflow="hidden"
								bg="surface.canvas"
								mt={showSidebar ? { base: 0, md: 2 } : 0}
								mr={showSidebar ? { base: 0, md: 2 } : 0}
								mb={showSidebar ? { base: 0, md: 1 } : 0}
								borderRadius={showSidebar ? { base: 0, md: '3xl' } : 0}
								borderWidth={showSidebar ? { base: 0, md: '1px' } : 0}
								borderColor="sidebar.border"
								boxShadow={
									showSidebar ? { base: 'none', md: '0 1px 2px 0 rgba(0, 0, 0, 0.08)' } : 'none'
								}
							>
								<Box
									ref={handleDesktopHeaderPortalRef}
									flexShrink={0}
									display={{ base: 'none', md: 'block' }}
								/>

								<Flex
									ref={mainContentRef}
									className="page-content-scrollable"
									data-test-id="app-main-content"
									flex="1"
									minH={0}
									direction="column"
									position="relative"
									overflowY="auto"
									overflowX="hidden"
									// No vertical padding so children handle their own padding.
									// This allow children to have full control over their layout, especially
									// when dealing with sticky footers or full-width components, etc.
									// e.g.: The tos acceptance page sets the page as 100vh,
									// if there is padding here it shows a scrollbar that is not desired.
									py={0}
								>
									<Outlet />

									{/* Sticky footer portal target for forms (e.g., Onboarding) */}
									{showFooter && (
										<Box
											ref={handleFooterPortalRef}
											mt="auto"
											position="fixed"
											bottom={0}
											left={0}
											right={0}
											w="full"
											bg="surface.canvas"
											borderTopWidth="1px"
											borderColor="border.default"
											px={{ base: 4, md: 6 }}
											py={{ base: 4, md: 4 }}
											zIndex={10}
										/>
									)}
								</Flex>
							</Flex>

							{/* Notifications Drawer - rendered within main content container */}
							{showSidebar && (
								<NotificationsDrawer
									isOpen={notificationsDrawer.isOpen}
									onClose={notificationsDrawer.close}
									containerRef={mainContentRef}
								/>
							)}
						</Flex>
					</Flex>
				</PageScrollContainerProvider>
			</AppFooterPortalProvider>
		</DesktopPropertyHeaderPortalProvider>
	);
};

const HouseTaskManagerWrapper: React.FC<{ house: House | null; children: React.ReactNode }> = ({ house, children }) => {
	return house ? <HouseTaskManagerProvider house={house}>{children}</HouseTaskManagerProvider> : <>{children}</>;
};

const InnerApp = ({ showSidebar, showFooter, currentPath }: PageLayoutProps) => {
	const { selectedHouse } = useHouseContext();
	const hasComposerFooter = hasComposerFooterForPath(currentPath);
	return (
		<HouseNotificationsProvider houseId={selectedHouse?.id}>
			<HouseTaskManagerWrapper house={selectedHouse}>
				<PageLayout showSidebar={showSidebar} showFooter={showFooter} currentPath={currentPath} />
				{selectedHouse && <GlobalOverlays hasFooter={showFooter} hasComposerFooter={hasComposerFooter} />}
			</HouseTaskManagerWrapper>
		</HouseNotificationsProvider>
	);
};

const NO_SIDEBAR_ROUTES = new Set([
	Routes.HOUSE_SETUP,
	Routes.HOUSE_SETUP_NEW,
	Routes.TOS_ACCEPTANCE,
	Routes.PROFILE_SETUP,
]);

const hasComposerFooterForPath = (currentPath: string) =>
	currentPath === Routes.DASHBOARD ||
	currentPath === Routes.RECORDS ||
	currentPath === Routes.MAINTENANCE ||
	/^\/property\/[^/]+(?:\/records|\/schedule|\/archive)?$/.test(currentPath);

export const Route = createFileRoute({
	beforeLoad: async ({ location }) => {
		const token = safeGetItem(ACCESS_TOKEN_KEY);

		if (!token) {
			logger.warn('No token found, redirecting to login');
			/*
			 * The consent page is the OAuth authorization endpoint, and its address is the
			 * client's whole request — a person bounced to sign in has to come back to that exact
			 * address, query and all, or the request is gone. Carried for that route only: every
			 * other page sends people to the dashboard after signing in, and changing where the
			 * whole app lands is not this route's decision to make.
			 */
			if (location.pathname === Routes.OAUTH_CONSENT) {
				throw redirect({ to: '/login', search: { redirect: location.href } } as never);
			}
			throw redirect({ to: '/login' });
		}
	},

	loader: async ({ location }) => {
		const currentPath = location.pathname;

		// Fetch user — all subsequent checks depend on this.
		// ensureQueryData returns from cache if fresh, otherwise fetches.
		// useAuth()'s useQuery shares the same key so it gets data synchronously.
		const user = await queryClient.ensureQueryData(readUsersMeUsersMeGetOptions());

		// Admins skip all routing gates
		if (hasAdminRole(user)) return { user };

		// First-time terms acceptance should happen before the profile/name gate. Version update checks
		// still live in TermsComplianceObserver because they need the active legal-version fetch.
		if (!user.tos_version && currentPath !== Routes.TOS_ACCEPTANCE) {
			throw redirect({ to: Routes.TOS_ACCEPTANCE, replace: true });
		}

		// Profile gate — must happen before house checks
		if (
			hasIncompleteProfile(user) &&
			currentPath !== Routes.PROFILE_SETUP &&
			!(currentPath === Routes.TOS_ACCEPTANCE && !user.tos_version)
		) {
			throw redirect({ to: Routes.PROFILE_SETUP, replace: true });
		}
		if (!hasIncompleteProfile(user) && currentPath === Routes.PROFILE_SETUP) {
			throw redirect({ to: Routes.PROPERTIES, replace: true });
		}

		// Allow the per-user pages and ToS to proceed without house checks: a key belongs to a
		// person rather than to a property, so holding none is not a reason to be sent away.
		// ToS compliance is handled by TermsComplianceObserver (needs a separate legal-version fetch).
		if (
			currentPath === Routes.TOS_ACCEPTANCE ||
			currentPath === Routes.PROFILE_SETUP ||
			currentPath === Routes.PROFILE ||
			currentPath === Routes.API_KEYS ||
			currentPath === Routes.OAUTH_CONSENT
		) {
			return { user };
		}

		const houses = await queryClient.ensureQueryData(readHousesHousesGetOptions());

		const anyHouseHasBeenSetup = houses.some(isHouseSetupComplete);

		// /setup/new is only reachable after payment — always allow
		if (currentPath === Routes.HOUSE_SETUP_NEW) return { user, houses };

		if (!anyHouseHasBeenSetup) {
			// Send users with no set-up property to the properties page, which surfaces any
			// incoming transfers to claim. Payment-just-confirmed is suppressed so the success
			// page can mount.
			const paymentJustConfirmed =
				sessionStorage.getItem('paymentJustConfirmed') === 'true' ||
				sessionStorage.getItem('proPaymentJustConfirmed') === 'true';

			if (!paymentJustConfirmed && currentPath !== Routes.HOUSE_SETUP && currentPath !== Routes.PROPERTIES) {
				throw redirect({ to: Routes.PROPERTIES, replace: true });
			}
		}

		return { user, houses };
	},

	component: function LayoutRouteComponent() {
		const currentPath = useRouterState({ select: (state) => state.location.pathname });
		// @ts-expect-error Set<Routes> vs string — currentPath is a string at runtime
		const showSidebar = !NO_SIDEBAR_ROUTES.has(currentPath);
		const showFooter = currentPath === Routes.HOUSE_SETUP || currentPath === Routes.HOUSE_SETUP_NEW;

		// Initialize inactivity logout monitoring for authenticated users
		useInactivityLogout();

		return (
			<SidebarCollapseProvider>
				<HouseProvider currentPath={currentPath}>
					<RecordStatusProvider>
						<AppNavigationProvider>
							<TaskProvider>
								<UpdateStatusProvider>
									<RightSidePanelProvider>
										<ChatHistoryProvider>
											<InnerApp
												showSidebar={showSidebar}
												showFooter={showFooter}
												currentPath={currentPath}
											/>
										</ChatHistoryProvider>
									</RightSidePanelProvider>
								</UpdateStatusProvider>
							</TaskProvider>
						</AppNavigationProvider>
					</RecordStatusProvider>

					{/* Terms Compliance Observer - handles redirect logic within router context */}
					<TermsComplianceObserver />
				</HouseProvider>
			</SidebarCollapseProvider>
		);
	},
});
